"use client";

import Counter from "@/components-shared/counter";
import { FloatInput } from "@/components-shared/ui/input";
import { FocusEventHandler, memo, useCallback, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Button } from "@/components-shared/ui/button";
import { Slider } from "@/components-shared/ui/slider";
import { dir } from "i18next";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  clearSearchParams,
  setSearchParams,
} from "@/redux/slices/searchParams.slice";
import Link from "next/link";
import useUpdateSearchParams from "@/lib/useUpdateSearchParams";

const Filters = () => {
  const locale = useLocale();
  const t = useTranslations("filters");
  const query = useAppSelector((state) => state.searchParams);
  const dispatch = useAppDispatch();
  const [showAllOptions, setShowAllOptions] = useState(false);

  const setCounter = useCallback(
    (name: keyof FiltersType, count: number) =>
      dispatch(setSearchParams({ [name]: count })),
    []
  );

  const minPrice = query?.minPrice || 1;
  const maxPrice = query?.maxPrice || 10000;

  const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    e.target.select();
  };

  useUpdateSearchParams(query);

  return (
    <nav className="sm:px-4 max-sm:w-full space-y-6">
      <span className="text-xl max-sm:hidden">{t("filterBy")}</span>
      <section>
        <div className="text-lg">
          <div className="flex justify-between items-end">
            {t("pricePerNight")}{" "}
            <span className="text-base text-gray-light">{t("currency")}</span>
          </div>
          <div className="flex gap-4 items-center my-4 *:w-full *:flex-1">
            <FloatInput
              placeholder={t("from")}
              containerClassName="w-full"
              type="number"
              min={0}
              onFocus={handleFocus}
              value={minPrice.toString().replace(/^0/, "")}
              onChange={(e) =>
                dispatch(
                  setSearchParams({
                    minPrice: Number(e.target.value.replace(/^0/, "")),
                  })
                )
              }
            />
            <FloatInput
              placeholder={t("to")}
              type="number"
              min={0}
              containerClassName="w-full"
              onFocus={handleFocus}
              value={maxPrice.toString().replace(/^0/, "")}
              onChange={(e) =>
                dispatch(
                  setSearchParams({
                    maxPrice: Number(e.target.value.replace(/^0/, "")),
                  })
                )
              }
            />
          </div>
          <Slider
            defaultValue={[1, 10000]}
            min={1}
            max={10000}
            value={[minPrice, maxPrice]}
            onValueChange={([minPrice, maxPrice]) =>
              dispatch(setSearchParams({ minPrice, maxPrice }))
            }
            minStepsBetweenThumbs={1}
            dir={dir(locale as string)}
          />
        </div>
      </section>
      <section>
        <span>{t("rating")}</span>
        <div className="space-y-3 my-4">
          <CheckboxItem
            name="rating"
            label={t("excellent")}
            value={"WONDERFUL"}
          />
          <CheckboxItem
            name="rating"
            label={t("veryGood")}
            value={"VERY_GOOD"}
          />
          <CheckboxItem name="rating" label={t("good")} value={"GOOD"} />
          <CheckboxItem
            name="rating"
            label={t("pleasant")}
            value={"PLEASANT"}
          />
          <CheckboxItem name="rating" label={t("fair")} value={"FAIR"} />
        </div>
      </section>
      <section>
        <span>{t("roomsAndFacilities")}</span>
        <div className="space-y-3 my-4 *:flex *:justify-between *:items-center *:text-xs">
          <div>
            <span>{t("numberOfRooms")}</span>
            <Counter
              small
              min={0}
              countNumber={query?.numOfBedrooms || 0}
              setCountNumber={setCounter.bind(null, "numOfBedrooms")}
            />
          </div>
          <div>
            <span>{t("singleBeds")}</span>
            <Counter
              small
              min={0}
              countNumber={query?.numOfSingleBeds || 0}
              setCountNumber={setCounter.bind(null, "numOfSingleBeds")}
            />
          </div>
          <div>
            <span>{t("masterBeds")}</span>
            <Counter
              small
              min={0}
              countNumber={query?.numOfMasterBeds || 0}
              setCountNumber={setCounter.bind(null, "numOfMasterBeds")}
            />
          </div>
          <div>
            <span>{t("bathrooms")}</span>
            <Counter
              small
              min={0}
              countNumber={query?.numOfBathrooms || 0}
              setCountNumber={setCounter.bind(null, "numOfBathrooms")}
            />
          </div>
        </div>
      </section>
      <section>
        <span>{t("amenities")}</span>
        <div className="grid grid-cols-2 gap-3 my-4">
          {[
            // General Facilities (مرافق عامة)
            [t("adultPool"), "adultPool"],
            [t("childrenPool"), "childrenPool"],
            [t("sharedPool"), "sharedPool"],
            [t("waterGames"), "waterGames"],
            [t("elevator"), "elevator"],
            [t("grillCorner"), "grill"],
            [t("airConditioner"), "airConditioning"],
            [t("wifi"), "wifi"],
            [t("balcony"), "balcony"],
            [t("gym"), "gym"],
            [t("parking"), "parking"],
            [t("garden"), "garden"],
            [t("outdoorSpace"), "outdoorSpace"],
            [t("washer"), "washer"],
            [t("speakers"), "speakers"],
            [t("billiards"), "billiards"],
            [t("massageChair"), "massageChair"],
            [t("sunbathingChair"), "sunbathingChair"],
            [t("projector"), "projector"],
            [t("cinemaRoom"), "cinemaRoom"],
            [t("flatScreenTV"), "flatScreenTV"],

            // Bathroom Facilities (مرافق الحمام)
            [t("shower"), "shower"],
            [t("bathTub"), "bathTub"],
            [t("jacuzzi"), "jacuzzi"],
            [t("hotWater"), "hotWater"],
            [t("bathroomSupplies"), "bathroomSupplies"],
            [t("towels"), "towels"],
            [t("hairDryer"), "hairDryer"],
            [t("bathrobe"), "bathrobe"],

            // Kitchen Facilities (مرافق المطبخ)
            [t("refrigerator"), "refrigerator"],
            [t("freezer"), "freezer"],
            [t("microwave"), "microwave"],
            [t("oven"), "oven"],
            [t("kettle"), "kettle"],
            [t("coffeeMaker"), "coffeeMaker"],
            [t("cookingUtensils"), "cookingUtensils"],
            [t("diningUtensils"), "diningUtensils"],
            [t("dishwasher"), "dishwasher"],
            [t("diningTable"), "diningTable"],

            // Additional Amenities (مرافق إضافية)
            [t("sauna"), "sauna"],
            [t("spa"), "spa"],
            [t("toys"), "toys"],
            [t("intelGames"), "intelGames"],
            [t("iron"), "iron"],
            [t("dryer"), "dryer"],
            [t("shampoo"), "shampoo"],
            [t("soap"), "soap"],
            [t("napkins"), "napkins"],
          ]
            .filter((_, i) => (showAllOptions ? true : i < 12))
            .map(([label, value]) => (
              <CheckboxItem
                name={"entertainment"}
                key={value}
                label={label}
                value={value}
              />
            ))}
          <button
            onClick={() => setShowAllOptions((prev) => !prev)}
            className="text-[12px] underline text-green-main text-right"
          >
            {showAllOptions ? t("showLess") : t("showMore")}
          </button>
        </div>
      </section>
      <div className="flex mt-8 *:flex-1 *:py-2 *:rounded-2xl gap-4">
        <Button
          variant={"destructive"}
          size={"sm"}
          className="!rounded-md"
          asChild
        >
          <Link
            onClick={() => dispatch(clearSearchParams())}
            href={{ pathname: "/all-units", query: { page: "1" } }}
          >
            {t("clear")}
          </Link>
        </Button>
      </div>
    </nav>
  );
};

export default memo(Filters);

const CheckboxItem = ({
  label,
  value,
  name,
}: {
  label: string;
  value: string;
  name: "cityId" | "rating" | "entertainment";
}) => {
  const dispatch = useAppDispatch();
  const q = useAppSelector((state) => state.searchParams);
  return (
    <div title={label} className="flex items-center gap-1">
      <input
        type="checkbox"
        id={value}
        className="checkbox [--chkbg:theme(colors.green.main)]"
        value={value}
        checked={q[name]?.includes(value)}
        onChange={() =>
          q[name]?.includes(value)
            ? dispatch(
                setSearchParams({
                  [name]: q[name].filter((v) => v !== value),
                })
              )
            : dispatch(
                setSearchParams({
                  [name]: [...(q[name] || []), value],
                })
              )
        }
      />
      <label
        htmlFor={value}
        className="text-[12px] cursor-pointer whitespace-nowrap truncate"
      >
        {label}
      </label>
    </div>
  );
};
